import { useState, useCallback } from 'react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ContactFormData, FormMessage } from '../types';

// Soomer API configuration
const SOOMER_API_URL = import.meta.env.VITE_SOOMER_API_URL || 'https://api.soomerlabs.com';
const APPLICATION_ID = import.meta.env.VITE_SOOMER_APPLICATION_ID || '';

interface UseContactFormReturn {
  formData: ContactFormData;
  emailError: string;
  phoneError: string;
  isSubmitting: boolean;
  formMessage: FormMessage;
  isFormValid: boolean;
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  handleSubmit: (e: React.FormEvent) => Promise<void>;
  resetForm: () => void;
}

const initialFormData: ContactFormData = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  message: ''
};

export function useContactForm(): UseContactFormReturn {
  const [formData, setFormData] = useState<ContactFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState<FormMessage>({ text: '', type: '' });
  const [emailError, setEmailError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const { executeRecaptcha } = useGoogleReCaptcha();

  // Format phone number as (XXX) XXX-XXXX
  const formatPhoneNumber = (value: string): string => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 3) {
      return cleaned;
    } else if (cleaned.length <= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    } else if (cleaned.length <= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else {
      return `${cleaned.slice(0, 1)} (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7, 11)}`;
    }
  };

  // Clean phone number (remove formatting)
  const cleanPhoneNumber = (value: string): string => {
    return value.replace(/\D/g, '');
  };

  // Validate email
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email.trim());
  };

  // Validate phone
  const isValidPhone = (phone: string): boolean => {
    const cleaned = cleanPhoneNumber(phone);
    return cleaned.length >= 10 && cleaned.length <= 11;
  };

  // Check if form is valid
  const isFormValid = useCallback((): boolean => {
    return (
      formData.firstName.trim() !== '' &&
      formData.lastName.trim() !== '' &&
      formData.email.trim() !== '' &&
      isValidEmail(formData.email) &&
      isValidPhone(formData.phone) &&
      formData.message.trim() !== ''
    );
  }, [formData]);

  // Handle input change
  const handleChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'phone') {
      setFormData(prev => ({ ...prev, [name]: formatPhoneNumber(value) }));
      if (value && !isValidPhone(value)) {
        setPhoneError('Please enter a valid phone number');
      } else {
        setPhoneError('');
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }

    // Validate email on change
    if (name === 'email') {
      if (value.trim() && !isValidEmail(value)) {
        setEmailError('Please enter a valid email address');
      } else {
        setEmailError('');
      }
    }
  }, []);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setEmailError('');
    setPhoneError('');
    setFormMessage({ text: '', type: '' });
  }, []);

  // Handle form submission
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isFormValid()) {
      setFormMessage({ text: 'Please fill in all fields correctly', type: 'error' });
      return;
    }

    if (!executeRecaptcha) {
      setFormMessage({ text: 'reCAPTCHA not ready. Please try again.', type: 'error' });
      return;
    }

    setIsSubmitting(true);

    try {
      // Execute reCAPTCHA v3 and get token
      const recaptchaToken = await executeRecaptcha('contact_form');

      const formDataToSend = new FormData();
      formDataToSend.append('name', `${formData.firstName.trim()} ${formData.lastName.trim()}`);
      formDataToSend.append('first_name', formData.firstName.trim());
      formDataToSend.append('last_name', formData.lastName.trim());
      formDataToSend.append('email', formData.email.trim());
      formDataToSend.append('phone', cleanPhoneNumber(formData.phone));
      formDataToSend.append('message', formData.message.trim());
      formDataToSend.append('g-recaptcha-response', recaptchaToken);

      const response = await fetch(`${SOOMER_API_URL}/api/v1/contact/`, {
        method: 'POST',
        body: formDataToSend,
        headers: {
          'Accept': 'application/json',
          'X-Soomer-Application-ID': APPLICATION_ID
        }
      });

      if (response.status === 201 || response.ok) {
        setFormMessage({ text: "Thank you! We'll be in touch soon.", type: 'success' });
        resetForm();

        // Clear success message after 5 seconds
        setTimeout(() => {
          setFormMessage({ text: '', type: '' });
        }, 5000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setFormMessage({
          text: errorData.message || 'Failed to send message. Please try again.',
          type: 'error'
        });
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormMessage({ text: 'Network error. Please try again later.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, isFormValid, resetForm, executeRecaptcha]);

  return {
    formData,
    emailError,
    phoneError,
    isSubmitting,
    formMessage,
    isFormValid: isFormValid(),
    handleChange,
    handleSubmit,
    resetForm
  };
}

export default useContactForm;
